import React, { FC, memo } from 'react';
import styled from 'styled-components';
import { FieldProps, Field } from 'formik';
import { useTranslation } from '@blocs.i18n';
import { FilterData, FilterValues } from '../../shared/types';
import { FieldGroup, Delimiter, Label, StyledFormikDatePicker } from '../../shared/styles';

const StyledDelimiter = styled(Delimiter)`
  margin: 0 0.5rem;
`;

const DatePickerWrapper = styled.div<{ $withDelimiter?: boolean }>`
  ${(props) =>
    !props.$withDelimiter &&
    `
    &:not(:last-child) {
      margin-right: 1rem;
    }
  `}
`;

type Props = {
  isClearable?: boolean;
  withDelimiter?: boolean;
  dateFormat?: 'standard' | 'short';
  dateRangeName: FilterData['name'];
  dateRangeMinName: FilterData['name'];
  dateRangeMaxName: FilterData['name'];
  dateRangeMinLabel?: FilterData['label'];
  dateRangeMaxLabel?: FilterData['label'];
  label?: FilterData['label'];
  startDatePopperPlacement?: string;
  endDatePopperPlacement?: string;
};

const DateRange: FC<Props> = ({
  isClearable = false,
  withDelimiter = true,
  dateFormat = 'short',
  startDatePopperPlacement = 'bottom-end',
  endDatePopperPlacement = 'bottom-end',
  dateRangeName,
  dateRangeMinName,
  dateRangeMaxName,
  dateRangeMinLabel,
  dateRangeMaxLabel,
  label,
}) => {
  const { t } = useTranslation();
  const currentDate = new Date();

  return (
    <Field>
      {({ form }: FieldProps<FilterValues>): JSX.Element => {
        const dateRangeMinValue = form.values[dateRangeName][dateRangeMinName];
        const dateRangeMaxValue = form.values[dateRangeName][dateRangeMaxName];

        return (
          <>
            {label && <Label>{label}</Label>}
            <FieldGroup>
              <DatePickerWrapper $withDelimiter={withDelimiter}>
                <StyledFormikDatePicker
                  name={`${dateRangeName}.${dateRangeMinName}`}
                  selected={dateRangeMinValue ? new Date(dateRangeMinValue) : null}
                  maxDate={dateRangeMaxValue ? new Date(dateRangeMaxValue) : null}
                  dateFormat={dateFormat}
                  isClearable={isClearable}
                  label={dateRangeMinLabel}
                  popperPlacement={startDatePopperPlacement}
                  testId={`${dateRangeName}.${dateRangeMinName}`}
                />
              </DatePickerWrapper>
              {withDelimiter && <StyledDelimiter>{t('common:filter.to')}</StyledDelimiter>}
              <DatePickerWrapper $withDelimiter={withDelimiter}>
                <StyledFormikDatePicker
                  name={`${dateRangeName}.${dateRangeMaxName}`}
                  selected={dateRangeMaxValue ? new Date(dateRangeMaxValue) : null}
                  minDate={dateRangeMinValue ? new Date(dateRangeMinValue) : currentDate}
                  dateFormat={dateFormat}
                  isClearable={isClearable}
                  label={dateRangeMaxLabel}
                  popperPlacement={endDatePopperPlacement}
                  testId={`${dateRangeName}.${dateRangeMaxName}`}
                />
              </DatePickerWrapper>
            </FieldGroup>
          </>
        );
      }}
    </Field>
  );
};

export default memo(DateRange);
